/**
 * CheckoutContinue — the focused "Continue to secure checkout" step
 * for a customer who selected a plan on the public /n2/plans page,
 * completed the auth funnel (sign in, register, verify email), and
 * should now land directly at Stripe Checkout for the plan they
 * picked — without navigating the full /upgrade list again.
 *
 * Route: /subscription/continue?plan=<productKey>&billing=<monthly|yearly>
 *
 * Behavior:
 *   • Public purchase env-flag OFF → refuse to render a checkout
 *     button. The whole PurchaseButton → CheckoutContinue path is
 *     dormant until the operator flips REACT_APP_PUBLIC_PURCHASE_ENABLED.
 *   • Missing / unknown / non-ready plan → redirect back to /upgrade
 *     so the customer sees the full list and never gets stranded on a
 *     confirmation for something we cannot charge.
 *   • Signed-out visitor → ProtectedRoute at the router level bounces
 *     them to /login?redirect=/subscription/continue?... The auth
 *     funnel preserves the query string through Register → VerifyEmail
 *     → SubscriptionSetup, so the customer returns here after signing
 *     up.
 *   • Confirmed → creates a Stripe Checkout Session for the exact
 *     product+billing displayed on this screen and redirects the
 *     browser to Stripe.
 *
 * The plain /upgrade page is UNCHANGED for direct visitors — a customer
 * navigating to /upgrade without ?plan= still sees the full list.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BillingRequestError,
  createCheckoutSession,
  type Billing,
} from '../services/stripeBillingService';
import {
  RESEARCH_PLANS,
  type PlanKey,
  type ResearchPlan,
} from '../n2/plansCatalog';
import { isPublicPurchaseEnabled } from '../n2/purchaseIntent';

// productConfig key (the /upgrade / stripeCheckoutSession vocabulary)
// → plansCatalog PlanKey (the /n2/plans display vocabulary). Kept
// alongside PurchaseButton's forward mapping so both live in one
// spot; PurchaseButton exports the forward map, this file imports
// the reverse.
const PRODUCT_TO_PLAN: Partial<Record<string, PlanKey>> = {
  premier: 'premier',
  spx: 'spx-only',
  es: 'es-only',
  'index-etf-research': 'index-etf-research',
  'futures-options-research': 'futures-options-research',
  'all-research': 'all-research',
};

function lookupPlan(productKey: string | null): ResearchPlan | null {
  if (!productKey) return null;
  const planKey = PRODUCT_TO_PLAN[productKey];
  if (!planKey) return null;
  return RESEARCH_PLANS.find((p) => p.key === planKey) || null;
}

function formatPrice(dollars: number, billing: Billing): string {
  const cadence = billing === 'yearly' ? 'year' : 'month';
  return `$${dollars.toFixed(2)} / ${cadence}`;
}

function friendlyBillingRequestError(err: BillingRequestError, planName: string): string {
  switch (err.code) {
    case 'RETIRED_FOR_NEW_PURCHASE':
      return `${planName} is no longer available for new subscriptions. Please choose a different plan.`;
    case 'PAYMENT_PROVIDER_NOT_CONFIGURED':
      return `${planName} is not yet available for online checkout. Please contact support.`;
    case 'ALREADY_ACTIVE':
      return `You already have an active ${planName} subscription.`;
    case 'INCLUDED_IN_ACTIVE_BUNDLE':
      return `${planName} is already included in a bundle you subscribe to.`;
    case 'ACTIVE_RESEARCH_MEMBERSHIP_EXISTS':
      return `You already have another research membership. Please change plans from your account settings.`;
    case 'PENDING_CHECKOUT_EXISTS_FOR_OTHER_PLAN':
      return `You have a checkout in progress for a different plan. Finish or cancel it before starting another.`;
    case 'APPLICATION_ONLY':
      return `${planName} is available by application only.`;
    case 'BILLING_INTERVAL_NOT_OFFERED':
      return `${planName} does not offer the selected billing interval.`;
    default:
      return err.message || 'Something went wrong starting checkout. Please try again.';
  }
}

export const CheckoutContinue: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const productKey = searchParams.get('plan');
  const billingRaw = searchParams.get('billing');
  const billing: Billing = billingRaw === 'yearly' ? 'yearly' : 'monthly';
  const plan = useMemo(() => lookupPlan(productKey), [productKey]);

  // Guard against a stranded confirmation screen. Any of these fall
  // through to /upgrade so the customer at least sees the plan list.
  const flagEnabled = isPublicPurchaseEnabled();
  const planIsPurchasable = plan && plan.purchase === 'ready';

  if (!flagEnabled || !plan || !planIsPurchasable) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Choose a plan</h1>
          <p style={bodyStyle}>
            {!flagEnabled
              ? 'Direct online checkout is not enabled yet. Please review the plans list to continue.'
              : !plan
                ? 'We could not find the plan you selected. Please pick from the list.'
                : `${plan.name} is not available for direct checkout yet. Please choose another plan or contact support.`}
          </p>
          <Link to="/upgrade" style={primaryButtonStyle}>See plans →</Link>
        </div>
      </div>
    );
  }

  const price = billing === 'yearly' && plan.yearlyPrice != null
    ? plan.yearlyPrice
    : plan.monthlyPrice;

  const handleContinue = async () => {
    if (submitting || !token || !productKey) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const { url } = await createCheckoutSession(token, productKey, billing);
      // Full-page redirect — Stripe requires it and this drops any
      // client-side state so the checkout is the sole authority for
      // what the customer pays for.
      window.location.assign(url);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof BillingRequestError) {
        setErrorMessage(friendlyBillingRequestError(err, plan.name));
      } else {
        setErrorMessage('Something went wrong starting checkout. Please try again.');
      }
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>Continue to secure checkout</h1>
        <p style={mutedStyle}>Review your selection before we send you to Stripe.</p>

        <div style={summaryStyle}>
          <div style={rowStyle}>
            <span style={labelStyle}>Plan</span>
            <span style={valueStyle}>{plan.name}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Coverage</span>
            <span style={valueStyle}>{plan.coverage}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Billing</span>
            <span style={valueStyle}>{billing === 'yearly' ? 'Annual' : 'Monthly'}</span>
          </div>
          <div style={{ ...rowStyle, borderTop: '1px solid #e5e0d5', paddingTop: 12 }}>
            <span style={labelStyle}>Price</span>
            <span style={{ ...valueStyle, fontWeight: 600 }}>{formatPrice(price, billing)}</span>
          </div>
        </div>

        {errorMessage && (
          <div style={errorStyle} role="alert">{errorMessage}</div>
        )}

        <button
          type="button"
          onClick={handleContinue}
          disabled={submitting || !user}
          style={{
            ...primaryButtonStyle,
            opacity: submitting || !user ? 0.6 : 1,
            cursor: submitting || !user ? 'wait' : 'pointer',
          }}
        >
          {submitting ? 'Redirecting to Stripe…' : 'Continue to secure checkout →'}
        </button>

        <div style={{ marginTop: 12, display: 'flex', gap: 16 }}>
          <button
            type="button"
            onClick={() => navigate('/n2/plans')}
            style={secondaryLinkStyle}
          >
            Change plan
          </button>
          <Link to="/upgrade" style={secondaryLinkStyle}>See all plans</Link>
        </div>

        <p style={{ ...mutedStyle, marginTop: 24, fontSize: 12 }}>
          Payment is processed by Stripe. You can cancel at any time from your
          account settings.
        </p>
      </div>
    </div>
  );
};

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '48px 16px',
  background: '#faf7f0',
};
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 480,
  background: '#ffffff',
  border: '1px solid #e5e0d5',
  borderRadius: 8,
  padding: '32px 28px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const titleStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 24,
  color: '#1a1613',
  fontFamily: 'Georgia, "Times New Roman", serif',
};
const mutedStyle: React.CSSProperties = {
  color: '#6a5f52',
  fontSize: 14,
  margin: '0 0 20px',
};
const bodyStyle: React.CSSProperties = {
  color: '#1a1613',
  fontSize: 15,
  lineHeight: 1.5,
  margin: '0 0 24px',
};
const summaryStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 16,
  background: '#f2ebdc',
  borderRadius: 6,
  marginBottom: 20,
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 12,
};
const labelStyle: React.CSSProperties = {
  color: '#8a7d70',
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};
const valueStyle: React.CSSProperties = {
  color: '#1a1613',
  fontSize: 15,
  textAlign: 'right',
};
const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '100%',
  textAlign: 'center',
  padding: '14px 18px',
  background: '#1a1613',
  color: '#faf7f0',
  border: 'none',
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
  textDecoration: 'none',
};
const secondaryLinkStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#4a4038',
  cursor: 'pointer',
  fontSize: 14,
  textDecoration: 'underline',
  padding: 0,
};
const errorStyle: React.CSSProperties = {
  background: '#fdecea',
  color: '#8a1c1c',
  border: '1px solid #f5b5b0',
  borderRadius: 6,
  padding: '12px 14px',
  fontSize: 14,
  marginBottom: 16,
};

export default CheckoutContinue;
